// Inline quick status change dropdown for issues table
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, Loader2 } from 'lucide-react'

const TRANSITIONS: Record<string, { label: string; value: string }[]> = {
  draft: [{ label: 'Submit Issue', value: 'open' }],
  open: [{ label: 'Start Working', value: 'in_progress' }],
  in_progress: [
    { label: 'Waiting Info', value: 'waiting_info' },
    { label: 'Mark Resolved', value: 'resolved' },
  ],
  waiting_info: [{ label: 'Resume Work', value: 'in_progress' }],
  resolved: [
    { label: 'Close Issue', value: 'closed' },
    { label: 'Reopen', value: 'in_progress' },
  ],
  closed: [{ label: 'Reopen Issue', value: 'open' }],
}

interface IssueQuickStatusChangeProps {
  issueId: string
  currentStatus: string
}

export function IssueQuickStatusChange({
  issueId, currentStatus,
}: IssueQuickStatusChangeProps): JSX.Element {
  const queryClient = useQueryClient()
  const transitions = TRANSITIONS[currentStatus] ?? []

  const mutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const updates: Record<string, unknown> = { status: newStatus }
      if (newStatus === 'resolved') {
        updates.resolved_at = new Date().toISOString()
      }
      if (newStatus === 'open' || newStatus === 'in_progress') {
        updates.resolved_at = null
        updates.resolved_by = null
      }
      const { error } = await supabase
        .from('issues')
        .update(updates as never)
        .eq('id', issueId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] })
    },
  })

  if (transitions.length === 0) return <></>

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={mutation.isPending}>
          {mutation.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <MoreHorizontal className="h-3.5 w-3.5" />
          }
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {transitions.map((t) => (
          <DropdownMenuItem key={t.value} onClick={() => mutation.mutate(t.value)}>
            {t.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
